import { useState } from "react";
import { AboutHeroData } from "@/types/cms";

type HeroData = AboutHeroData;

interface Props {
  data: HeroData;
  onChange: (d: HeroData) => void;
}

export default function AboutHeroEditor({ data, onChange }: Props) {
  const [lang, setLang] = useState<"ar" | "en">("ar");
  const [newWord, setNewWord] = useState("");

  const currentWords = lang === "ar" ? (data.typewriterWords || []) : (data.typewriterWordsEn || []);

  const addWord = () => {
    if (!newWord.trim()) return;
    if (lang === "ar") {
      onChange({ ...data, typewriterWords: [...(data.typewriterWords || []), newWord.trim()] });
    } else {
      onChange({ ...data, typewriterWordsEn: [...(data.typewriterWordsEn || []), newWord.trim()] });
    }
    setNewWord("");
  };

  const removeWord = (i: number) => {
    if (lang === "ar") {
      onChange({ ...data, typewriterWords: (data.typewriterWords || []).filter((_, idx) => idx !== i) });
    } else {
      onChange({ ...data, typewriterWordsEn: (data.typewriterWordsEn || []).filter((_, idx) => idx !== i) });
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Language Switcher Bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
        <div>
          <h3 className="text-sm font-bold text-gray-800">لغة المحتوى (Content Language)</h3>
          <p className="text-xs text-gray-500">اختر اللغة لتعديل قسم Hero في صفحة من نحن</p>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setLang("ar")}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              lang === "ar" ? "bg-white text-[#2E4E45] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            العربية (AR)
          </button>
          <button
            type="button"
            onClick={() => setLang("en")}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              lang === "en" ? "bg-white text-[#2E4E45] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            English (EN)
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <div className="w-10 h-10 rounded-xl bg-[#2E4E45]/10 flex items-center justify-center">
          <i className="ri-layout-top-line text-[#2E4E45] text-lg" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-800">
            {lang === "ar" ? "قسم Hero (الرئيسي)" : "Hero Section (English)"}
          </h3>
          <p className="text-xs text-gray-500">الصورة الكبيرة والعنوان الرئيسي في أعلى الصفحة</p>
        </div>
      </div>

      {/* Hero image */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">صورة الخلفية</label>
        <div className="relative rounded-xl overflow-hidden border border-gray-200 mb-3" style={{ height: "160px" }}>
          <img src={data.image} alt="Hero" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-l from-black/60 to-transparent flex items-end p-4">
            <span className="text-white text-xs font-medium bg-black/40 px-2 py-1 rounded">معاينة الخلفية</span>
          </div>
        </div>
        <input
          type="url"
          value={data.image}
          onChange={(e) => onChange({ ...data, image: e.target.value })}
          dir="ltr"
          placeholder="رابط الصورة..."
          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10 transition-all font-mono text-xs"
        />
      </div>

      {/* Badge & Title */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {lang === "ar" ? "نص الشارة (Badge)" : "Badge (EN)"}
          </label>
          <input
            type="text"
            value={lang === "ar" ? (data.badge || "") : (data.badgeEn || "")}
            onChange={(e) => onChange({ ...data, [lang === "ar" ? "badge" : "badgeEn"]: e.target.value })}
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10 transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {lang === "ar" ? "العنوان الرئيسي" : "Main Title (EN)"}
          </label>
          <input
            type="text"
            value={lang === "ar" ? (data.title || "") : (data.titleEn || "")}
            onChange={(e) => onChange({ ...data, [lang === "ar" ? "title" : "titleEn"]: e.target.value })}
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10 transition-all"
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {lang === "ar" ? "الوصف" : "Description (EN)"}
        </label>
        <textarea
          rows={3}
          value={lang === "ar" ? (data.description || "") : (data.descriptionEn || "")}
          onChange={(e) => onChange({ ...data, [lang === "ar" ? "description" : "descriptionEn"]: e.target.value })}
          dir={lang === "ar" ? "rtl" : "ltr"}
          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10 transition-all resize-none"
        />
      </div>

      {/* Typewriter words */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          <i className="ri-cursor-line ml-1.5 text-[#C8A96E]" />
          {lang === "ar" ? "كلمات الـ Typewriter (العربية)" : "Typewriter Words (English)"}
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {currentWords.map((word, i) => (
            <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2E4E45]/8 text-[#2E4E45] text-xs font-medium">
              {word}
              <button
                type="button"
                onClick={() => removeWord(i)}
                className="w-3.5 h-3.5 flex items-center justify-center hover:text-red-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addWord()}
            placeholder={lang === "ar" ? "أضف كلمة جديدة..." : "Add new word..."}
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] transition-all"
          />
          <button
            type="button"
            onClick={addWord}
            className="px-4 py-2 rounded-lg bg-[#2E4E45] text-white text-sm hover:bg-[#243d36] transition-colors whitespace-nowrap cursor-pointer"
          >
            {lang === "ar" ? "إضافة" : "Add"}
          </button>
        </div>
      </div>

      {/* CTA buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {lang === "ar" ? "نص الزر الأول (CTA)" : "Primary Button Text (EN)"}
          </label>
          <input
            type="text"
            value={lang === "ar" ? (data.ctaPrimary || "") : (data.ctaPrimaryEn || "")}
            onChange={(e) => onChange({ ...data, [lang === "ar" ? "ctaPrimary" : "ctaPrimaryEn"]: e.target.value })}
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10 transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {lang === "ar" ? "نص الزر الثاني" : "Secondary Button Text (EN)"}
          </label>
          <input
            type="text"
            value={lang === "ar" ? (data.ctaSecondary || "") : (data.ctaSecondaryEn || "")}
            onChange={(e) => onChange({ ...data, [lang === "ar" ? "ctaSecondary" : "ctaSecondaryEn"]: e.target.value })}
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10 transition-all"
          />
        </div>
      </div>
    </div>
  );
}
